import { fetchListings } from '@app/api'
import { DEFAULT_LANGUAGE } from '@app/utils/regional'

import { missingAttachmentListingIds } from './constants'
import type {
  AttachmentListingSemaphore,
  LoadAttachmentListingsParams,
} from './types'

export const loadAttachmentListings = async ({
  trackedListingIds,
  buyerCulture,
  buyerLanguage,
  fetchedListings,
  fetchingAttachmentListingIds,
  setFetchedListings,
  stateListings,
  getListingSemaphore,
  isCancelled,
}: LoadAttachmentListingsParams): Promise<void> => {
  if (!trackedListingIds.length) {
    return
  }

  const preferredLang = buyerLanguage ?? buyerCulture ?? DEFAULT_LANGUAGE
  const acquiredSemaphores: AttachmentListingSemaphore[] = []
  let idsToFetch: number[] = []

  try {
    for (const listingId of trackedListingIds) {
      const semaphore = getListingSemaphore(listingId)
      await semaphore.acquire()
      acquiredSemaphores.push(semaphore)
    }

    idsToFetch = trackedListingIds.filter((listingId) => {
      if (stateListings.has(listingId)) {
        return false
      }

      if (fetchedListings.has(listingId)) {
        return false
      }

      return true
    })

    if (!idsToFetch.length) {
      trackedListingIds.forEach((id) => fetchingAttachmentListingIds.delete(id))
      return
    }

    const idsToFetchSet = new Set(idsToFetch)
    trackedListingIds.forEach((id) => {
      if (!idsToFetchSet.has(id)) {
        fetchingAttachmentListingIds.delete(id)
      }
    })

    const listings = await fetchListings(idsToFetch, preferredLang)

    if (isCancelled()) {
      return
    }

    const receivedIds = new Set(listings.map((listing) => listing.id))

    idsToFetch.forEach((listingId) => {
      if (!receivedIds.has(listingId)) {
        missingAttachmentListingIds.add(listingId)
      }
    })

    if (listings.length) {
      setFetchedListings((prev) => {
        const next = new Map(prev)
        listings.forEach((listing) => {
          if (listing) {
            next.set(listing.id, listing)
          }
        })
        return next
      })
    }
  } catch (error) {
    console.error(
      'Failed to fetch listings for message attachments',
      trackedListingIds,
      error
    )
  } finally {
    idsToFetch.forEach((id) => fetchingAttachmentListingIds.delete(id))
    acquiredSemaphores.forEach((semaphore) => semaphore.release())
  }
}
